<script>
import debounce from 'lodash/debounce';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import BlockingContainer from '@components/BlockingContainer';
import Layout from '@layouts/main';
import ServiceProvider from '@models/ServiceProvider';
import HeaderComponent from '@components/HeaderComponent';

export default {
  page() {
    return {
      title: this.$t('titles.service_providers'),
    };
  },
  components: {
    Layout,
    FontAwesomeIcon,
    BlockingContainer,
    HeaderComponent,
  },
  data() {
    return {
      sortBy: 'name',
      sortDesc: false,
      totalRows: null,
      perPage: 20,
      currentPage: 1,
      fieldsTable: [
        { key: 'id', sortable: true, class: 'w-40' },
        { key: 'name', label: this.$t('table_labels.company_name'), sortable: true },
        { key: 'contact', label: this.$t('table_labels.contact'), sortable: true },
        { key: 'email', label: this.$t('table_labels.email'), sortable: true },
        { key: 'phone_number', label: this.$t('table_labels.phone_number'), sortable: false },
        { key: 'town', label: this.$t('table_labels.town'), sortable: true },
        { key: 'country', label: this.$t('table_labels.country'), sortable: true },
        { key: 'actions', label: this.$t('table_labels.actions'), sortable: false },
      ],
      isBusy: false,
      filters: {
        search: null,
      },
    };
  },
  computed: {
    sortDir() {
      return this.sortDesc ? '-' : '';
    },
  },
  watch: {
    filters: {
      handler() {
        this.$refs.asyncTable.refresh();
      },
      immediate: false,
      deep: true,
    },
  },
  methods: {
    itemsProvider(ctx) {
      return ServiceProvider.orderBy(this.sortDir + ctx.sortBy)
        .where('search', this.filters.search)
        .page(this.currentPage)
        .limit(ctx.perPage)
        .get()
        .then((response) => {
          const items = response.data;
          this.totalRows = response.total;
          return items || [];
        })
        .catch(() => []);
    },
    async deleteEntity(id) {
      if (confirm(this.$t('alerts.delete_entity', { entity: this.$t('entities.service_provider') }))) {
        this.$wait.start('service-provider processing');
        const serviceProvider = new ServiceProvider({ id });
        await serviceProvider.delete();
        this.$wait.end('service-provider processing');
        this.$refs.asyncTable.refresh();
      }
    },
    updateFilter: debounce(function(value) {
      this.filters.search = value;
    }, 500),
    goToEditServiceProvider(serviceProviderId) {
      const routeData = this.$router.resolve({ name: 'serviceProviders.edit', params: { id: serviceProviderId } });
      window.open(routeData.href, '_blank');
    },
  },
};
</script>

<template>
  <Layout>
    <b-row class="mb-4">
      <b-col>
        <HeaderComponent :title="$t('titles.service_providers')" :button-text="$t('titles.create_service_provider')" @button-pressed="$router.push({ name: 'serviceProviders.create' })"></HeaderComponent>
      </b-col>
    </b-row>
    <b-row class="mb-4">
      <b-col sm="12" md="12" lg="4">
        <HeaderComponent>
          <template v-slot:header>
            <span>{{ $t('fields.search') }}</span>&nbsp;
            <small class="pt-01">
              {{
                $t('search.message', {
                  entity: [$t('search.entities.name'), $t('search.entities.contact'), $t('search.entities.email')].join(', '),
                })
              }}
            </small>
          </template>
        </HeaderComponent>
        <b-input-group>
          <b-form-input :value="filters.search" class="form-control-sm" :placeholder="$t('placeholders.search')" @input="updateFilter"></b-form-input>
          <b-input-group-append>
            <b-btn variant="secondary" size="sm" class="text-white border-gray" :disabled="!filters.search" @click="filters.search = ''">{{ $t('actions.clear') }}</b-btn>
          </b-input-group-append>
        </b-input-group>
      </b-col>
    </b-row>
    <div id="reports-index" class="position-relative">
      <BlockingContainer v-if="$wait.is('contacts processing') || isBusy"></BlockingContainer>
      <b-table
        ref="asyncTable"
        small
        striped
        :items="itemsProvider"
        stacked="md"
        :busy.sync="isBusy"
        :current-page="currentPage"
        :per-page="perPage"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :fields="fieldsTable"
        :no-sort-reset="true"
        :show-empty="true"
      >
       <!-- custom sorting icons -->
       <template #head()="{ label, field: { key, sortable } }">
              {{ label }}
              <template v-if="sortable">
                <!-- Custom icons -->
                <template>
                  <FontAwesomeIcon v-if="sortBy !== key" :icon="['fas', 'arrows-alt-v']" />
                  <FontAwesomeIcon v-else-if="sortDesc" :icon="['fas', 'arrow-down']" />
                  <FontAwesomeIcon v-else :icon="['fas', 'arrow-up']" />
                </template>
              </template>
        </template>
        <template v-slot:cell(actions)="row">
          <div class="d-flex">
            <b-btn class="mr-2" size="sm" :disabled="row.item.notes == null" :variant="row.detailsShowing ? 'secondary' : 'outline-secondary'" @click.stop="row.toggleDetails">
              <FontAwesomeIcon :icon="['fas', 'sticky-note']" />
            </b-btn>
            <b-btn class="mr-2" size="sm" variant="outline-primary" @click.stop="goToEditServiceProvider(row.item.id)">
              <FontAwesomeIcon :icon="['fas', 'edit']" />
            </b-btn>
            <b-btn size="sm" variant="outline-danger" @click.stop="deleteEntity(row.item.id)">
              <FontAwesomeIcon :icon="['fas', 'times-circle']" />
            </b-btn>
          </div>
        </template>
        <template v-slot:row-details="row">
          <!-- eslint-disable vue/no-v-html -->
          <b-card v-html="row.item.notes">
          </b-card>
        </template>
        <template v-slot:cell(empty)="row">
          <div class="text-center">{{ $t('messages.no_items') }}</div>
        </template>
      </b-table>
    </div>

    <b-row>
      <b-col sm="12">
        <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage" />
      </b-col>
    </b-row>
  </Layout>
</template>
